import { HTTPClient } from "@abaco/web-common/src/HTTP";
import { PagedResults, PagingParams } from "@abaco/web-common/src/Paging";
import { DocumentType } from "../models/Document";
import {
    WorkerActivityIn,
    WorkerData,
    WorkerIn,
    WorkerOut,
    Worker,
    WorkerActionData,
    toWorkerActionOut,
    Role,
    WorkerWithActivities,
    TeamSubject,
    WorkersReportData
} from "../models/Worker";
import { Plot, PlotGroup } from "@abaco/portal-global-state/src/modules/global-state/models/Plot";

export default class WorkerApi {
    private http: HTTPClient;
    private rootPath: string;

    constructor(http: HTTPClient, rootPath: string) {
        this.http = http;
        this.rootPath = rootPath;
    }

    public async getWorkers(
        pk_cuaa: number,
        onlyActive: boolean,
        paging: PagingParams,
        filter: string | null
    ): Promise<PagedResults<WorkerWithActivities>> {
        const res = await this.http.get(`${this.rootPath}/v1/plots/subjects/${pk_cuaa}/dictionary_workers`, {
            params: {
                offset: paging.offset,
                count: paging.count,
                filter: filter ? `^subject_full_name like ${filter.toUpperCase()}` : undefined
            }
        });

        if (onlyActive) {
            return {
                ...res.data,
                records: res.data.records.filter((w: WorkerWithActivities) =>
                    w.worker_roles.find(r => r.fg_active === 1)
                )
            };
        } else {
            return res.data;
        }
    }

    public async getWorkersList(pk_cuaa: number): Promise<WorkerWithActivities[]> {
        const res = await this.http.get(`${this.rootPath}/v1/plots/subjects/${pk_cuaa}/dictionary_workers`);
        return res.data.records;
    }

    public async getWorkersByUser(pk_cuaa: number): Promise<PagedResults<Worker>> {
        const res = await this.http.get(`${this.rootPath}/v1/plots/subjects/${pk_cuaa}/dictionary_workers`, {
            params: {
                byUser: true
            }
        });

        return res.data;
    }

    public async getWorkerById(pk_cuaa: number, id: number): Promise<any> {
        const res = await this.http.get(`${this.rootPath}/v1/plots/subjects/${pk_cuaa}/dictionary_workers`, {
            params: {
                filter: `subject_id eq ${id}`
            }
        });

        return res;
    }

    async getWorkersRoles(subject_id: number): Promise<Role[]> {
        const filters = ["num_declared gt 0"];
        const res = await this.http.get(`${this.rootPath}/v1/workers/${subject_id}/roles`, {
            params: {
                filter: filters,
                offset: "0",
                count: "300"
            }
        });

        return res.data.records;
    }

    async getWorkersQualifications(subject_id: number): Promise<DocumentType[]> {
        const res = await this.http.get(
            `${this.rootPath}/v1/plots/subjects/${subject_id}/dictionary_worker_document_types`,
            {}
        );

        return res.data.records;
    }

    public async getTeamMembers(subject_id: number): Promise<TeamSubject[]> {
        const res = await this.http.get(`${this.rootPath}/v1/teams/${subject_id}/involved`, {
            params: {
                offset: "0",
                count: "300"
            }
        });

        return res.data.records;
    }

    public async getWorkersReport(
        subject_id: number,
        campaign: number,
        month: number,
        plot_name: string | null,
        plot_group_id: number | null
    ): Promise<WorkersReportData[]> {
        const res = await this.http.get(
            `${this.rootPath}/v1/plots/dashboard/subjects/${subject_id}/${campaign}/planned_workers`,
            {
                params: {
                    month: month,
                    plot_name: plot_name ? plot_name : undefined,
                    plot_group_id: plot_group_id ? plot_group_id : undefined
                }
            }
        );

        return res.data;
    }

    public async checkWorkerAgenda(pk_cuaa: number): Promise<Worker[]> {
        const res = await this.http.get(`${this.rootPath}/v1/plots/subjects/${pk_cuaa}/dictionary_workers`, {
            params: {
                byUser: true,
                hasPrivateAgenda: true
            }
        });

        return res.data.records;
    }

    public async addWorker(
        pk_cuaa: number,
        campaign: number,
        action_id: number,
        newWorker: WorkerOut
    ): Promise<WorkerActivityIn> {
        const res = await this.http.post(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/activities/${action_id}/workers`,
            newWorker
        );
        return res.data;
    }
    public async deleteWorker(
        pk_cuaa: number,
        campaign: number,
        action_id: number,
        action_resource_id: number
    ): Promise<WorkerActivityIn> {
        const res = await this.http.delete(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/activities/${action_id}/workers/${action_resource_id}`
        );
        return res.data;
    }

    public async addActionWorker(
        pk_cuaa: number,
        campaign: number,
        execution_id: number,
        newWorker: WorkerOut
    ): Promise<WorkerIn> {
        const res = await this.http.post(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/actions/${execution_id}/workers`,
            newWorker
        );
        return res.data;
    }

    public async deleteActionWorker(
        pk_cuaa: number,
        campaign: number,
        execution_id: number,
        execution_resource_id: number
    ): Promise<WorkerIn> {
        const res = await this.http.delete(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/actions/${execution_id}/workers/${execution_resource_id}`
        );
        return res.data;
    }

    public async editActionWorker(pk_cuaa: number, campaign: number, worker: WorkerActionData): Promise<WorkerIn> {
        const res = await this.http.put(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/actions/${worker.execution_id}/workers/${worker.execution_resource_id}`,
            {
                worker_id: worker.worker.subject_id,
                quantity: worker.quantity,
                sco_unit_measure: worker.uom.sco_unit_of_measure
            }
        );
        return res.data;
    }

    public async editWorker(pk_cuaa: number, campaign: number, worker: WorkerData): Promise<WorkerActivityIn> {
        const res = await this.http.put(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/activities/${worker.action_id}/workers/${worker.action_resource_id}`,
            {
                worker_id: worker.worker.subject_id,
                quantity: worker.quantity,
                sco_unit_measure: worker.uom.sco_unit_of_measure
            }
        );
        return res.data;
    }

    public async assignActivityToWorker(
        pk_cuaa: number,
        campaign: number,
        action_id: number,
        worker: WorkerOut
    ): Promise<any> {
        const res = await this.http.post(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/activities/${action_id}/workers`,
            {
                worker_id: worker.worker_id,
                quantity: worker.quantity,
                sco_unit_measure: worker.sco_unit_measure
            }
        );
        return res.data;
    }

    public async deleteActivityToWorker(
        pk_cuaa: number,
        campaign: number,
        action_id: number,
        worker_actionResourceId: number
    ): Promise<any> {
        const res = await this.http.delete(
            `${this.rootPath}/v1/plots/subjects/${pk_cuaa}/${campaign}/activities/${action_id}/workers/${worker_actionResourceId}`
        );
        return res.data;
    }

    public async getPlotGroups(pkCuaa: number, paging: PagingParams): Promise<PagedResults<PlotGroup>> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/plots/subjects/${pkCuaa}/plot_groups`, {
            params: {
                offset: paging.offset,
                count: paging.count
            }
        });
        return res.data;
    }

}
